/**
 * CustomFieldsEditor — renders custom field inputs in CardDetailModal.
 * For each board field definition:
 * - Boolean: Radix Checkbox
 * - Dropdown-single: Radix Select
 * - Dropdown-multi: custom multi-select (checkboxes in a Radix Popover)
 * - Text: <input type="text"> with blur-to-save
 *
 * Auto-saves on change via PATCH /v1/boards/{boardId}/cards/{cardId}.
 *
 * Validates: Requirements 2.1, 2.2, 2.3, 2.4, 2.5, 2.6, 2.7
 */

import * as React from 'react';
import * as Popover from '@radix-ui/react-popover';
import { Checkbox } from '../ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { cn } from '../../lib/utils';
import * as api from '../../services/api-client';
import { CustomFieldDefinition, useActiveBoard } from '../../state/active-board-context';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface CustomFieldsEditorProps {
  readonly boardId: string;
  readonly cardId: string;
  readonly customFields: Record<string, boolean | string | string[]>;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const MAX_TEXT_LENGTH = 200;
const NONE_VALUE = '__none__';

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function CustomFieldsEditor({
  boardId,
  cardId,
  customFields,
}: CustomFieldsEditorProps): React.ReactElement | null {
  const { state } = useActiveBoard();
  const definitions = state.customFieldDefinitions;

  const [localValues, setLocalValues] = React.useState<
    Record<string, boolean | string | string[]>
  >({ ...customFields });

  // Sync local state when external customFields change (e.g., WebSocket update)
  React.useEffect(() => {
    setLocalValues({ ...customFields });
  }, [customFields]);

  const saveField = React.useCallback(
    async (fieldId: string, value: boolean | string | string[]) => {
      try {
        await api.patch(`/v1/boards/${boardId}/cards/${cardId}`, {
          customFields: { [fieldId]: value },
        });
      } catch {
        // Revert on failure
        setLocalValues((prev) => {
          const defaultVal = getDefaultValue(fieldId, definitions);
          const revertedVal = customFields[fieldId] ?? defaultVal;
          if (revertedVal === undefined) return prev;
          return { ...prev, [fieldId]: revertedVal };
        });
      }
    },
    [boardId, cardId, customFields, definitions],
  );

  if (definitions.length === 0) {
    return null;
  }

  const sortedDefinitions = [...definitions].sort((a, b) => a.sort - b.sort);

  return (
    <section aria-label="Custom fields" className="mb-4">
      <h3 className="text-xs font-medium text-muted-foreground uppercase mb-2">
        Custom Fields
      </h3>
      <div className="space-y-3">
        {sortedDefinitions.map((def) => (
          <CustomFieldRow
            key={def.fieldId}
            definition={def}
            value={localValues[def.fieldId]}
            onValueChange={(value) => {
              setLocalValues((prev) => ({ ...prev, [def.fieldId]: value }));
              saveField(def.fieldId, value);
            }}
            onTextBlur={(value) => {
              setLocalValues((prev) => ({ ...prev, [def.fieldId]: value }));
              saveField(def.fieldId, value);
            }}
            localTextValue={localValues[def.fieldId] as string | undefined}
            onLocalTextChange={(value) => {
              setLocalValues((prev) => ({ ...prev, [def.fieldId]: value }));
            }}
          />
        ))}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Individual field row
// ---------------------------------------------------------------------------

interface CustomFieldRowProps {
  readonly definition: CustomFieldDefinition;
  readonly value: boolean | string | string[] | undefined;
  readonly onValueChange: (value: boolean | string | string[]) => void;
  readonly onTextBlur: (value: string) => void;
  readonly localTextValue: string | undefined;
  readonly onLocalTextChange: (value: string) => void;
}

function CustomFieldRow({
  definition,
  value,
  onValueChange,
  onTextBlur,
  localTextValue,
  onLocalTextChange,
}: CustomFieldRowProps): React.ReactElement {
  const fieldLabelId = `custom-field-label-${definition.fieldId}`;

  return (
    <div className="flex items-center gap-3">
      <label
        id={fieldLabelId}
        className="text-sm font-medium text-card-foreground min-w-[120px] shrink-0"
      >
        {definition.name}
      </label>
      <div className="flex-1 min-w-0">
        {definition.fieldType === 'boolean' && (
          <BooleanField
            fieldId={definition.fieldId}
            labelId={fieldLabelId}
            value={value as boolean | undefined}
            onChange={onValueChange}
          />
        )}
        {definition.fieldType === 'dropdown-single' && (
          <DropdownSingleField
            fieldId={definition.fieldId}
            labelId={fieldLabelId}
            options={definition.options}
            value={value as string | undefined}
            onChange={onValueChange}
          />
        )}
        {definition.fieldType === 'dropdown-multi' && (
          <DropdownMultiField
            fieldId={definition.fieldId}
            labelId={fieldLabelId}
            options={definition.options}
            value={value as string[] | undefined}
            onChange={onValueChange}
          />
        )}
        {definition.fieldType === 'text' && (
          <TextField
            fieldId={definition.fieldId}
            labelId={fieldLabelId}
            value={localTextValue}
            onChange={onLocalTextChange}
            onBlur={onTextBlur}
          />
        )}
        {definition.fieldType === 'number' && (
          <NumberField
            fieldId={definition.fieldId}
            labelId={fieldLabelId}
            value={localTextValue}
            onChange={onLocalTextChange}
            onBlur={onTextBlur}
          />
        )}
        {definition.fieldType === 'linked-to-list' && (
          <LinkedToListField
            fieldId={definition.fieldId}
            labelId={fieldLabelId}
            value={value as string | undefined}
          />
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Boolean field
// ---------------------------------------------------------------------------

interface BooleanFieldProps {
  readonly fieldId: string;
  readonly labelId: string;
  readonly value: boolean | undefined;
  readonly onChange: (value: boolean) => void;
}

function BooleanField({
  fieldId,
  labelId,
  value,
  onChange,
}: BooleanFieldProps): React.ReactElement {
  const checked = value === true;

  return (
    <Checkbox
      id={`custom-field-input-${fieldId}`}
      checked={checked}
      onCheckedChange={(state) => {
        onChange(state === true);
      }}
      aria-labelledby={labelId}
    />
  );
}

// ---------------------------------------------------------------------------
// Dropdown-single field
// ---------------------------------------------------------------------------

interface DropdownSingleFieldProps {
  readonly fieldId: string;
  readonly labelId: string;
  readonly options: readonly string[];
  readonly value: string | undefined;
  readonly onChange: (value: string) => void;
}

function DropdownSingleField({
  fieldId,
  labelId,
  options,
  value,
  onChange,
}: DropdownSingleFieldProps): React.ReactElement {
  return (
    <Select
      value={value || NONE_VALUE}
      onValueChange={(newValue) => {
        onChange(newValue === NONE_VALUE ? '' : newValue);
      }}
    >
      <SelectTrigger
        id={`custom-field-input-${fieldId}`}
        aria-labelledby={labelId}
        className="h-8 text-sm"
      >
        <SelectValue placeholder="None" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={NONE_VALUE}>None</SelectItem>
        {options.map((option) => (
          <SelectItem key={option} value={option}>
            {option}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

// ---------------------------------------------------------------------------
// Dropdown-multi field
// ---------------------------------------------------------------------------

interface DropdownMultiFieldProps {
  readonly fieldId: string;
  readonly labelId: string;
  readonly options: readonly string[];
  readonly value: string[] | undefined;
  readonly onChange: (value: string[]) => void;
}

function DropdownMultiField({
  fieldId,
  labelId,
  options,
  value,
  onChange,
}: DropdownMultiFieldProps): React.ReactElement {
  const [isOpen, setIsOpen] = React.useState(false);
  const selected = value ?? [];

  const displayText =
    selected.length === 0
      ? 'None'
      : selected.length === 1
        ? selected[0]
        : `${selected.length} selected`;

  const handleToggleOption = React.useCallback(
    (option: string) => {
      const isSelected = selected.includes(option);
      const newSelection = isSelected
        ? selected.filter((s) => s !== option)
        : [...selected, option];
      onChange(newSelection);
    },
    [selected, onChange],
  );

  return (
    <Popover.Root open={isOpen} onOpenChange={setIsOpen}>
      <Popover.Trigger asChild>
        <button
          id={`custom-field-input-${fieldId}`}
          type="button"
          aria-labelledby={labelId}
          aria-haspopup="listbox"
          className={cn(
            'flex h-8 w-full items-center justify-between rounded-md border border-input',
            'bg-background px-3 py-1 text-sm ring-offset-background',
            'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
          )}
        >
          <span className={cn('truncate', selected.length === 0 && 'text-muted-foreground')}>
            {displayText}
          </span>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="h-4 w-4 shrink-0 opacity-50"
            aria-hidden="true"
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </button>
      </Popover.Trigger>

      <Popover.Portal>
        <Popover.Content
          sideOffset={4}
          align="start"
          className={cn(
            'z-50 w-56 overflow-hidden rounded-md border',
            'bg-popover text-popover-foreground shadow-md',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
            'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
          )}
        >
          <ul
            role="listbox"
            aria-label={`Options for multi-select`}
            aria-multiselectable="true"
            className="max-h-60 overflow-y-auto p-1"
          >
            {options.map((option) => {
              const isChecked = selected.includes(option);
              return (
                <li
                  key={option}
                  role="option"
                  aria-selected={isChecked}
                  className={cn(
                    'flex cursor-pointer select-none items-center gap-2 rounded-sm px-3 py-1.5 text-sm',
                    'transition-colors hover:bg-accent hover:text-accent-foreground',
                  )}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => handleToggleOption(option)}
                >
                  <Checkbox
                    checked={isChecked}
                    tabIndex={-1}
                    aria-hidden="true"
                    className="pointer-events-none"
                  />
                  <span className="truncate">{option}</span>
                </li>
              );
            })}
          </ul>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

// ---------------------------------------------------------------------------
// Text field
// ---------------------------------------------------------------------------

interface TextFieldProps {
  readonly fieldId: string;
  readonly labelId: string;
  readonly value: string | undefined;
  readonly onChange: (value: string) => void;
  readonly onBlur: (value: string) => void;
}

function TextField({
  fieldId,
  labelId,
  value,
  onChange,
  onBlur,
}: TextFieldProps): React.ReactElement {
  const textValue = value ?? '';

  return (
    <input
      id={`custom-field-input-${fieldId}`}
      type="text"
      aria-labelledby={labelId}
      value={textValue}
      maxLength={MAX_TEXT_LENGTH}
      onChange={(e) => onChange(e.target.value)}
      onBlur={() => onBlur(textValue)}
      className={cn(
        'flex h-8 w-full rounded-md border border-input bg-background px-3 py-1 text-sm',
        'ring-offset-background placeholder:text-muted-foreground',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
      )}
      placeholder="Enter text…"
    />
  );
}

// ---------------------------------------------------------------------------
// Number field
// ---------------------------------------------------------------------------

interface NumberFieldProps {
  readonly fieldId: string;
  readonly labelId: string;
  readonly value: string | undefined;
  readonly onChange: (value: string) => void;
  readonly onBlur: (value: string) => void;
}

function NumberField({
  fieldId,
  labelId,
  value,
  onChange,
  onBlur,
}: NumberFieldProps): React.ReactElement {
  const textValue = value ?? '';

  return (
    <input
      id={`custom-field-input-${fieldId}`}
      type="number"
      step="any"
      aria-labelledby={labelId}
      value={textValue}
      onChange={(e) => onChange(e.target.value)}
      onBlur={() => onBlur(textValue)}
      className={cn(
        'flex h-8 w-full rounded-md border border-input bg-background px-3 py-1 text-sm',
        'ring-offset-background placeholder:text-muted-foreground',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
      )}
      placeholder="Enter number…"
    />
  );
}

// ---------------------------------------------------------------------------
// Linked-to-list field (read-only)
// ---------------------------------------------------------------------------

interface LinkedToListFieldProps {
  readonly fieldId: string;
  readonly labelId: string;
  readonly value: string | undefined;
}

function LinkedToListField({
  fieldId,
  labelId,
  value,
}: LinkedToListFieldProps): React.ReactElement {
  const displayValue = value || 'Not set';

  return (
    <span
      id={`custom-field-input-${fieldId}`}
      aria-labelledby={labelId}
      className={cn(
        'inline-flex h-8 items-center rounded-md border border-input bg-muted/50 px-3 py-1 text-sm',
        !value && 'text-muted-foreground italic',
      )}
    >
      {displayValue}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getDefaultValue(
  fieldId: string,
  definitions: readonly CustomFieldDefinition[],
): boolean | string | string[] | undefined {
  const def = definitions.find((d) => d.fieldId === fieldId);
  if (!def) return undefined;
  switch (def.fieldType) {
    case 'boolean':
      return false;
    case 'dropdown-single':
      return '';
    case 'dropdown-multi':
      return [];
    case 'text':
      return '';
    case 'number':
      return '';
    case 'linked-to-list':
      return '';
    default:
      return undefined;
  }
}
